import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Globe, MapPin, Plane, Ship, Truck, ArrowRight, Search,
  ChevronRight, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RoutesPage = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedOrigin, setSelectedOrigin] = useState('');

  const fetchCountries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/countries`);
      if (res.ok) {
        const data = await res.json();
        setCountries(data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Get unique regions - memoized
  const regions = useMemo(() => 
    [...new Set(countries.map(c => c.region))].sort(),
    [countries]
  );

  // Filter countries - memoized
  const filteredCountries = useMemo(() => 
    countries.filter(country => {
      const matchesSearch = country.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === 'all' || country.region === selectedRegion;
      return matchesSearch && matchesRegion;
    }),
    [countries, searchTerm, selectedRegion]
  );

  // Group by region - memoized
  const countriesByRegion = useMemo(() => 
    filteredCountries.reduce((acc, country) => {
      if (!acc[country.region]) {
        acc[country.region] = [];
      }
      acc[country.region].push(country);
      return acc;
    }, {}),
    [filteredCountries]
  );

  // Featured routes (sample data)
  const featuredRoutes = [
    {
      origin: 'Netherlands',
      destination: 'United States',
      services: ['Ocean Freight', 'Air Freight'],
      transitTime: '12-18 days (Ocean)',
      popular: true
    },
    {
      origin: 'Netherlands',
      destination: 'China',
      services: ['Ocean Freight', 'Air Freight'],
      transitTime: '28-38 days (Ocean)',
      popular: true
    },
    {
      origin: 'Netherlands',
      destination: 'Germany',
      services: ['Road Freight'],
      transitTime: '1-2 days',
      popular: true
    },
    {
      origin: 'Netherlands',
      destination: 'United Kingdom',
      services: ['Road Freight', 'Ocean Freight'],
      transitTime: '2-3 days',
      popular: true
    },
  ];

  const getServiceIcon = (service) => {
    if (service.includes('Air')) return Plane;
    if (service.includes('Ocean')) return Ship;
    return Truck;
  };

  return (
    <div className="min-h-screen bg-steel-50" data-testid="routes-page">
      {/* Hero Section */}
      <section className="bg-navy-900 pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium tracking-wider uppercase mb-6">
              {t('routesPage.badge')}
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              {t('routesPage.title')}
            </h1>
            <p className="text-steel-300 text-lg mb-10">
              {t('routesPage.subtitle', { count: countries.length })}
            </p>
            
            {/* Quick Route Search */}
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 max-w-2xl mx-auto">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-steel-300 text-sm mb-2 block text-left">
                    {t('routesPage.origin')}
                  </label>
                  <Select value={selectedOrigin} onValueChange={setSelectedOrigin}>
                    <SelectTrigger className="bg-white border-0">
                      <SelectValue placeholder={t('routesPage.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.filter(c => c.featured).map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-steel-300 text-sm mb-2 block text-left">
                    {t('routesPage.destination')}
                  </label>
                  <Select>
                    <SelectTrigger className="bg-white border-0">
                      <SelectValue placeholder={t('routesPage.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.filter(c => c.featured).map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Link to="/quote" className="w-full">
                    <Button className="btn-accent w-full h-10">
                      {t('routesPage.getQuote')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Routes */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold text-sm font-bold tracking-widest uppercase mb-4 block">
              {t('routesPage.popularRoutes')}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy-800">
              {t('routesPage.mostRequestedRoutes')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredRoutes.map((route) => (
              <motion.div
                key={`${route.origin}-${route.destination}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Star className="w-5 h-5 text-gold fill-gold" />
                      <span className="text-xs text-steel-500 bg-steel-100 px-2 py-1 rounded-full">
                        {route.transitTime}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1">
                        <p className="text-sm text-steel-500">{t('routesPage.from')}</p>
                        <p className="font-semibold text-navy-800">{route.origin}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gold" />
                      <div className="flex-1">
                        <p className="text-sm text-steel-500">{t('routesPage.to')}</p>
                        <p className="font-semibold text-navy-800">{route.destination}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {route.services.map((service) => {
                        const Icon = getServiceIcon(service);
                        return (
                          <span key={service} className="flex items-center gap-1 text-xs bg-navy-100 text-navy-800 px-2 py-1 rounded-full">
                            <Icon className="w-3 h-3" />
                            {service}
                          </span>
                        );
                      })}
                    </div>
                    
                    <Link to="/quote">
                      <Button variant="outline" className="w-full group-hover:bg-gold group-hover:text-navy-800 group-hover:border-gold">
                        {t('routesPage.requestQuote')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries by Region */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy-800 mb-6">
              {t('routesPage.countriesWeServe')}
            </h2>
            
            {/* Filters */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                <Input
                  placeholder={t('routesPage.searchCountry')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('routesPage.region')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('routesPage.allRegions')}</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-gold animate-pulse mx-auto mb-4" />
              <p className="text-steel-600">{t('routesPage.loadingCountries')}</p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(countriesByRegion).sort().map(([region, regionCountries]) => (
                <motion.div
                  key={region}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Globe className="w-6 h-6 text-gold" />
                    <h3 className="font-outfit text-2xl font-bold text-navy-800">{region}</h3>
                    <span className="text-sm text-steel-500 bg-steel-100 px-2 py-1 rounded-full">
                      {regionCountries.length} {t('routesPage.countries')}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {regionCountries.map(country => (
                      <Link 
                        key={country.code}
                        to={`/quote?destination=${country.name}`}
                        className="group"
                      >
                        <div className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-steel-100 group-hover:border-gold/30">
                          <div className="flex items-center gap-3">
                            <MapPin className="w-4 h-4 text-gold" />
                            <span className="text-sm font-medium text-navy-800 group-hover:text-gold transition-colors">
                              {country.name}
                            </span>
                          </div>
                          {country.featured && (
                            <div className="mt-2">
                              <span className="text-xs text-gold bg-gold/10 px-2 py-0.5 rounded-full">
                                {t('routesPage.popular')}
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-navy-900">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-white mb-4">
              {t('routesPage.transportationServices')}
            </h2>
            <p className="text-steel-300">
              {t('routesPage.multipleOptions')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                id: 'air',
                icon: Plane,
                title: t('routesPage.airFreight'),
                desc: t('routesPage.airFreightDesc'),
                time: '2-7 ' + t('routesPage.days')
              },
              {
                id: 'ocean',
                icon: Ship,
                title: t('routesPage.oceanFreight'),
                desc: t('routesPage.oceanFreightDesc'),
                time: '15-45 ' + t('routesPage.days')
              },
              {
                id: 'road',
                icon: Truck,
                title: t('routesPage.roadFreight'),
                desc: t('routesPage.roadFreightDesc'),
                time: '1-10 ' + t('routesPage.days')
              }
            ].map((service) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <service.icon className="w-8 h-8 text-gold" />
                </div>
                <h3 className="font-outfit text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-steel-400 mb-4">{service.desc}</p>
                <span className="inline-block bg-white/10 text-white text-sm px-4 py-2 rounded-full">
                  {service.time}
                </span>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/quote">
              <Button className="btn-accent h-14 px-10 text-base">
                {t('routesPage.requestQuote')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoutesPage;
